import { useState, useRef, useEffect, useCallback } from 'react';

import { createBloomAnimation, type BloomAnimation } from './bloomAnimation.ts';

export interface AnimatingEmojiData extends BloomAnimation {
	id: number;
	x: number;
	y: number;
	emojiId: string;
}

export const useBloomAnimation = (): {
	animatingEmojis: AnimatingEmojiData[];
	triggerBloomAnimation: (x: number, y: number, emojiId: string) => void;
} => {
	const [animatingEmojis, setAnimatingEmojis] = useState<AnimatingEmojiData[]>([]);
	const animationIdRef = useRef(0);
	const animationTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

	useEffect(() => {
		const timers = animationTimersRef.current;
		return () => {
			timers.forEach((timer) => clearTimeout(timer));
			timers.clear();
		};
	}, []);

	const triggerBloomAnimation = useCallback((x: number, y: number, emojiId: string): void => {
		const animation = createBloomAnimation(
			(Math.random() - 0.5) * 100,
			(Math.random() - 0.5) * 30,
			(Math.random() - 0.5) * 40,
			-120 - Math.random() * 80,
		);

		const id = ++animationIdRef.current;

		setAnimatingEmojis((prev) => [...prev, { id, x, y, emojiId, ...animation }]);

		const timer = setTimeout(() => {
			animationTimersRef.current.delete(timer);
			setAnimatingEmojis((prev) => prev.filter((e) => e.id !== id));
		}, 1300);

		animationTimersRef.current.add(timer);
	}, []);

	return { animatingEmojis, triggerBloomAnimation };
};
